import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  AudioCallReliableEventName,
  AudioCallReliableSendResult,
  SOCKET_EVENT_NAMES,
} from './web-socket.interfaces';

export type AudioCallConnectionDebugTone = 'neutral' | 'success' | 'warning' | 'error';
export type AudioCallConnectionDebugEventDirection = 'incoming' | 'outgoing' | 'local' | 'system';

export interface AudioCallConnectionDebugParams {
  [key: string]: string | number;
}

export interface AudioCallConnectionDebugStatus {
  actionKey: string;
  eventKey?: string;
  params?: AudioCallConnectionDebugParams;
  tone: AudioCallConnectionDebugTone;
}

export interface AudioCallConnectionDebugEntry extends AudioCallConnectionDebugStatus {
  source: string;
  stage: string;
  methodName?: string;
  eventName?: string;
  eventDirection?: AudioCallConnectionDebugEventDirection;
  timestamp: number;
  detail?: unknown;
}

interface AudioCallConnectionDebugTrackOptions {
  detail?: unknown;
  eventDirection?: AudioCallConnectionDebugEventDirection;
  eventName?: string;
  methodName?: string;
  updateCurrentStatus?: boolean;
}

interface AudioCallConnectionDebugSessionContext {
  orderId: number | null;
  role: 'customer' | 'executor' | null;
}

interface AudioCallConnectionDebugMappedStatus {
  status: AudioCallConnectionDebugStatus;
  updateCurrentStatus: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class AudioCallConnectionDebugService {
  public readonly currentStatus$: Observable<AudioCallConnectionDebugStatus | null>;
  public readonly currentEntry$: Observable<AudioCallConnectionDebugEntry | null>;
  public readonly entries$: Observable<AudioCallConnectionDebugEntry[]>;

  private readonly currentStatusSubject: BehaviorSubject<AudioCallConnectionDebugStatus | null> =
    new BehaviorSubject<AudioCallConnectionDebugStatus | null>(null);
  private readonly currentEntrySubject: BehaviorSubject<AudioCallConnectionDebugEntry | null> =
    new BehaviorSubject<AudioCallConnectionDebugEntry | null>(null);
  private readonly entriesSubject: BehaviorSubject<AudioCallConnectionDebugEntry[]> =
    new BehaviorSubject<AudioCallConnectionDebugEntry[]>([]);
  private readonly debugStackFrameSkipNames: string[] = [
    'extractStackFrameName',
    'logAudioCallReliableStep',
    'mapReliableStepToStatus',
    'normalizeMethodName',
    'resolveCallerMethodName',
    'resolveConnectionDebugEventLabel',
    'shouldSkipDebugStackFrame',
    'trackReliableReceiveStatus',
    'traceIncomingReliableEvent',
    'traceReliableResult',
    'traceReliableStep',
    'track',
    'trackConnectionDebugStatus',
    'trackDebugStatus',
  ];
  private readonly maxEntries: number = 250;
  private active: boolean = false;
  private sessionContext: AudioCallConnectionDebugSessionContext = {
    orderId: null,
    role: null,
  };

  constructor() {
    this.currentStatus$ = this.currentStatusSubject.asObservable();
    this.currentEntry$ = this.currentEntrySubject.asObservable();
    this.entries$ = this.entriesSubject.asObservable();
  }

  public activate(orderId?: number | null, role?: 'customer' | 'executor' | null): void {
    this.active = true;
    this.sessionContext = {
      orderId: typeof orderId === 'number' && Number.isFinite(orderId) ? orderId : null,
      role: role ?? null,
    };
    this.entriesSubject.next([]);
    this.currentStatusSubject.next(null);
    this.currentEntrySubject.next(null);
  }

  public deactivate(): void {
    this.active = false;
    this.sessionContext = {
      orderId: null,
      role: null,
    };
    this.entriesSubject.next([]);
    this.currentStatusSubject.next(null);
    this.currentEntrySubject.next(null);
  }

  public setOrderContext(orderId?: number | null, role?: 'customer' | 'executor' | null): void {
    this.sessionContext = {
      orderId: typeof orderId === 'number' && Number.isFinite(orderId) ? orderId : this.sessionContext.orderId,
      role: role ?? this.sessionContext.role,
    };
  }

  public track(
    source: string,
    stage: string,
    status: AudioCallConnectionDebugStatus,
    options?: AudioCallConnectionDebugTrackOptions,
  ): void {
    if (!this.active) return;
    const resolvedMethodName: string | undefined = options?.methodName ?? this.resolveCallerMethodName();

    const entry: AudioCallConnectionDebugEntry = {
      ...status,
      source,
      stage,
      ...(resolvedMethodName ? {methodName: resolvedMethodName} : {}),
      ...(options?.eventName ? {eventName: options.eventName} : {}),
      ...(options?.eventDirection ? {eventDirection: options.eventDirection} : {}),
      timestamp: Date.now(),
      ...(options?.detail !== undefined ? {detail: options.detail} : {}),
    };
    const nextEntries: AudioCallConnectionDebugEntry[] = [
      ...this.entriesSubject.value,
      entry,
    ];
    this.entriesSubject.next(nextEntries.slice(-this.maxEntries));

    if (options?.updateCurrentStatus !== false) {
      this.currentStatusSubject.next(status);
      this.currentEntrySubject.next(entry);
    }

    console.info('[AudioCallDebug]', {
      source: entry.source,
      stage: entry.stage,
      methodName: entry.methodName ?? null,
      eventName: entry.eventName ?? null,
      eventDirection: entry.eventDirection ?? null,
      actionKey: entry.actionKey,
      eventKey: entry.eventKey ?? null,
      params: entry.params ?? null,
      tone: entry.tone,
      detail: entry.detail ?? null,
      orderId: this.sessionContext.orderId,
      role: this.sessionContext.role,
      timestamp: entry.timestamp,
    });
  }

  public traceReliableStep(
    eventName: AudioCallReliableEventName,
    step: string,
    payload: { [key: string]: unknown },
  ): void {
    const mappedStatus: AudioCallConnectionDebugMappedStatus | null =
      this.mapReliableStepToStatus(eventName, step, payload);
    if (!mappedStatus) return;

    this.track(
      'socket',
      `reliable.${eventName}.${step}`,
      mappedStatus.status,
      {
        detail: payload,
        eventDirection: 'outgoing',
        eventName: eventName,
        updateCurrentStatus: mappedStatus.updateCurrentStatus,
      },
    );
  }

  public traceReliableResult(eventName: AudioCallReliableEventName, result: AudioCallReliableSendResult): void {
    const eventKey: string | undefined = this.resolveEventKey(eventName);
    if (!eventKey) return;
    if (result.delivered) {
      this.track(
        'socket',
        `reliable.${eventName}.confirmed`,
        {
          actionKey: 'call.audioCall.debugStatus.confirmed',
          eventKey,
          tone: 'success',
        },
        {
          detail: result,
          eventDirection: 'outgoing',
          eventName: eventName,
        },
      );
      return;
    }

    const statusValue: string = typeof result?.status === 'string' ? result.status : '';
    if (statusValue === 'socket_not_ready') {
      this.track(
        'socket',
        `reliable.${eventName}.socket_not_ready`,
        {
          actionKey: 'call.audioCall.debugStatus.socketNotReady',
          eventKey,
          tone: 'warning',
        },
        {
          detail: result,
          eventDirection: 'outgoing',
          eventName: eventName,
        },
      );
      return;
    }

    if (statusValue === 'ack_timeout') {
      this.track(
        'socket',
        `reliable.${eventName}.ack_timeout`,
        {
          actionKey: 'call.audioCall.debugStatus.waitingAck',
          eventKey,
          tone: 'warning',
        },
        {
          detail: result,
          eventDirection: 'outgoing',
          eventName: eventName,
        },
      );
      return;
    }

    if (statusValue === 'receipt_timeout') {
      this.track(
        'socket',
        `reliable.${eventName}.receipt_timeout`,
        {
          actionKey: 'call.audioCall.debugStatus.waitingReceipt',
          eventKey,
          tone: 'warning',
        },
        {
          detail: result,
          eventDirection: 'outgoing',
          eventName: eventName,
        },
      );
      return;
    }

    this.track(
      'socket',
      `reliable.${eventName}.failed`,
      {
        actionKey: 'call.audioCall.debugStatus.failed',
        eventKey,
        tone: 'error',
      },
      {
        detail: result,
        eventDirection: 'outgoing',
        eventName: eventName,
      },
    );
  }

  public traceIncomingReliableEvent(eventName: AudioCallReliableEventName, payload: { [key: string]: unknown }): void {
    const eventKey: string | undefined = this.resolveEventKey(eventName);
    if (!eventKey) return;

    this.track(
      'socket',
      `incoming.${eventName}`,
      {
        actionKey: 'call.audioCall.debugStatus.received',
        eventKey,
        tone: 'neutral',
      },
      {
        detail: payload,
        eventDirection: 'incoming',
        eventName: eventName,
        updateCurrentStatus: eventName !== SOCKET_EVENT_NAMES.AUDIO_CALL_CANDIDATE,
      },
    );
  }

  private resolveCallerMethodName(): string | undefined {
    const error: Error = new Error();
    if (typeof error.stack !== 'string' || !error.stack) {
      return undefined;
    }

    const stackLines: string[] = error.stack
      .split('\n')
      .map((line: string): string => line.trim())
      .filter((line: string): boolean => line.length > 0);

    for (const stackLine of stackLines) {
      const extractedName: string | null = this.extractStackFrameName(stackLine);
      if (!extractedName) continue;

      const normalizedName: string = this.normalizeMethodName(extractedName);
      if (!normalizedName || this.shouldSkipDebugStackFrame(normalizedName)) {
        continue;
      }

      return normalizedName;
    }

    return undefined;
  }

  private extractStackFrameName(stackLine: string): string | null {
    if (stackLine === 'Error') return null;

    const chromeMatch: RegExpMatchArray | null = stackLine.match(/^at\s+(.+?)\s+\(/);
    if (chromeMatch?.[1]) {
      return chromeMatch[1];
    }

    const safariMatch: RegExpMatchArray | null = stackLine.match(/^([^@]+)@/);
    if (safariMatch?.[1]) {
      return safariMatch[1];
    }

    const chromeNoParenMatch: RegExpMatchArray | null = stackLine.match(/^at\s+(.+)$/);
    if (chromeNoParenMatch?.[1] && !chromeNoParenMatch[1].includes('/')) {
      return chromeNoParenMatch[1];
    }

    return null;
  }

  private normalizeMethodName(methodName: string): string {
    return methodName
      .trim()
      .replace(/^async\s+/, '')
      .replace(/^new\s+/, '')
      .replace(/^Object\./, '')
      .replace(/^Generator\./, '');
  }

  private shouldSkipDebugStackFrame(methodName: string): boolean {
    const shortMethodName: string = methodName.split('.').pop() ?? methodName;
    if (!shortMethodName) return true;

    return this.debugStackFrameSkipNames.includes(shortMethodName);
  }

  private mapReliableStepToStatus(
    eventName: AudioCallReliableEventName,
    step: string,
    payload: { [key: string]: unknown },
  ): AudioCallConnectionDebugMappedStatus | null {
    const eventKey: string | undefined = this.resolveEventKey(eventName);
    if (!eventKey) return null;

    const updateCurrentStatus: boolean = eventName !== SOCKET_EVENT_NAMES.AUDIO_CALL_CANDIDATE;
    const attempt: number | null = this.normalizeNumber(payload?.attempt) ?? null;
    const maxAttempts: number | null = this.normalizeNumber(payload?.maxAttempts) ?? null;

    if (step === 'send_attempt') {
      return {
        status: {
          actionKey: 'call.audioCall.debugStatus.sending',
          eventKey,
          ...(attempt && maxAttempts ? {params: {attempt, maxAttempts}} : {}),
          tone: 'neutral',
        },
        updateCurrentStatus,
      };
    }

    if (step === 'send_blocked_socket_not_ready') {
      return {
        status: {
          actionKey: 'call.audioCall.debugStatus.socketNotReady',
          eventKey,
          tone: 'warning',
        },
        updateCurrentStatus,
      };
    }

    if (step === 'ack_result') {
      const ackReceived: boolean = payload?.ack_received === true;
      const ackDelivered: boolean = payload?.ack_delivered === true;
      const ackRetryable: boolean = payload?.ack_retryable === true;

      if (!ackReceived) {
        return {
          status: {
            actionKey: 'call.audioCall.debugStatus.waitingAck',
            eventKey,
            tone: 'warning',
          },
          updateCurrentStatus,
        };
      }

      if (ackDelivered) {
        return {
          status: {
            actionKey: 'call.audioCall.debugStatus.waitingReceipt',
            eventKey,
            tone: 'neutral',
          },
          updateCurrentStatus,
        };
      }

      if (ackRetryable) {
        return {
          status: {
            actionKey: 'call.audioCall.debugStatus.retrying',
            eventKey,
            ...(attempt && maxAttempts ? {params: {attempt, maxAttempts}} : {}),
            tone: 'warning',
          },
          updateCurrentStatus,
        };
      }

      return {
        status: {
          actionKey: 'call.audioCall.debugStatus.failed',
          eventKey,
          tone: 'error',
        },
        updateCurrentStatus,
      };
    }

    if (step === 'receipt_result') {
      const receiptReceived: boolean = payload?.receipt_received === true;
      const receiptStatus: unknown = payload?.receipt_status;

      if (!receiptReceived) {
        return {
          status: {
            actionKey: 'call.audioCall.debugStatus.waitingReceipt',
            eventKey,
            tone: 'warning',
          },
          updateCurrentStatus,
        };
      }

      if (receiptStatus === 'processed' || receiptStatus === 'duplicate') {
        return {
          status: {
            actionKey: 'call.audioCall.debugStatus.confirmed',
            eventKey,
            tone: 'success',
          },
          updateCurrentStatus,
        };
      }

      if (receiptStatus === 'ignored') {
        return {
          status: {
            actionKey: 'call.audioCall.debugStatus.ignored',
            eventKey,
            tone: 'warning',
          },
          updateCurrentStatus,
        };
      }

      if (receiptStatus === 'failed') {
        return {
          status: {
            actionKey: 'call.audioCall.debugStatus.failed',
            eventKey,
            tone: 'error',
          },
          updateCurrentStatus,
        };
      }
    }

    return null;
  }

  private resolveEventKey(eventName: AudioCallReliableEventName): string | undefined {
    if (eventName === SOCKET_EVENT_NAMES.AUDIO_CALL_RESPONSE) {
      return 'call.audioCall.debugEvents.response';
    }
    if (eventName === SOCKET_EVENT_NAMES.AUDIO_CALL_CUSTOMER_READY) {
      return 'call.audioCall.debugEvents.customerReady';
    }
    if (eventName === SOCKET_EVENT_NAMES.AUDIO_CALL_OFFER) {
      return 'call.audioCall.debugEvents.offer';
    }
    if (eventName === SOCKET_EVENT_NAMES.AUDIO_CALL_ANSWER) {
      return 'call.audioCall.debugEvents.answer';
    }
    if (eventName === SOCKET_EVENT_NAMES.AUDIO_CALL_CANDIDATE) {
      return 'call.audioCall.debugEvents.candidate';
    }
    if (eventName === SOCKET_EVENT_NAMES.AUDIO_CALL_EXIT) {
      return 'call.audioCall.debugEvents.exit';
    }
    if (eventName === SOCKET_EVENT_NAMES.AUDIO_CALL_EXIT_FROM_BACKEND) {
      return 'call.audioCall.debugEvents.exitFromBackend';
    }
    if (eventName === SOCKET_EVENT_NAMES.AUDIO_CALL_PARTNER_CONNECTED_STATUS) {
      return 'call.audioCall.debugEvents.partnerStatus';
    }
    if (eventName === SOCKET_EVENT_NAMES.AUDIO_CALL_REQUEST_RECONNECT) {
      return 'call.audioCall.debugEvents.requestReconnect';
    }
    if (eventName === SOCKET_EVENT_NAMES.AUDIO_CALL_CUSTOMER_READY_RECONNECT) {
      return 'call.audioCall.debugEvents.customerReadyReconnect';
    }

    return undefined;
  }

  private normalizeNumber(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return Math.floor(value);
    }
    return null;
  }
}
